import type { JoinMethod } from '@orbit/api-types'
import { buildDateRange } from '@components/TripOverview/core/overviewShared'
import { countLabel } from '@/utils/plural'

/**
 * The Join page's pure core: what the invitation says, which action the page
 * offers, and the path/method plumbing between the code-entry modal and the
 * page. No React, no network — tests run in node.
 */

/** The public `joinPreview` response, narrowed to what the page renders. */
export interface JoinPreview {
	tripInfo: { code: string; name: string; startDate: string | null; dayCount: number }
	tripOwner: { firstName: string; lastName: string; profilePicture: string | null }
	/** Active members already on the trip, the inviter included. */
	memberCount: number
}

/** Everything the invitation panel shows, already worded. */
export interface JoinInvitation {
	tripName: string
	inviterName: string
	inviterInitial: string
	inviterAvatarUrl: string | null
	/** "Apr 19 – May 4, 2026", or null when the trip has no start date. */
	dateRange: string | null
	/** "16 days", or null when the trip has no days yet. */
	dayLabel: string | null
	/** "Alice and 4 others are going". */
	goingLabel: string
}

const FALLBACK_INVITER = 'Someone'

const goingLabel = (inviter: string, memberCount: number): string => {
	const others = Math.max(memberCount - 1, 0)
	if (others === 0) return `${inviter} is going`
	return `${inviter} and ${countLabel(others, 'other')} are going`
}

export const buildJoinInvitation = (preview: JoinPreview): JoinInvitation => {
	const firstName = preview.tripOwner.firstName.trim()
	// Profiles arrive as typed ("andres"); the invitation always opens with a capital.
	const inviterName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : FALLBACK_INVITER
	const { startDate, dayCount } = preview.tripInfo

	return {
		tripName: preview.tripInfo.name,
		inviterName,
		inviterInitial: firstName ? firstName.charAt(0).toUpperCase() : '?',
		inviterAvatarUrl: preview.tripOwner.profilePicture ?? null,
		dateRange: buildDateRange(startDate, dayCount),
		dayLabel: dayCount > 0 ? countLabel(dayCount, 'day') : null,
		goingLabel: goingLabel(inviterName, preview.memberCount),
	}
}

/** How the person reached the join — the wire enum, shared with the server. */
export type { JoinMethod }

const VIA_PARAM = 'via'

/** In-app path of the Join page for a code; `code` marks a hand-typed entry. */
export const joinTripPath = (tripCode: string, method: JoinMethod = 'link'): string => {
	const path = `/join/${encodeURIComponent(tripCode.trim().toUpperCase())}`
	return method === 'code' ? `${path}?${VIA_PARAM}=code` : path
}

export const joinMethodFromSearch = (search: string): JoinMethod =>
	new URLSearchParams(search).get(VIA_PARAM) === 'code' ? 'code' : 'link'

/** What the page knows about the visitor's membership of the previewed trip. */
export type MembershipStatus = 'loading' | 'member' | 'not-member' | 'unknown'

/** What the right-hand panel does: `join` runs on its own, the others wait on the person. */
export type JoinAction = 'sign-in' | 'checking' | 'join' | 'open'

export const deriveJoinAction = (input: {
	signedIn: boolean
	membership: MembershipStatus
}): JoinAction => {
	if (!input.signedIn) return 'sign-in'
	switch (input.membership) {
		case 'loading':
			return 'checking'
		case 'member':
			return 'open'
		default:
			// A failed membership lookup must not block the join: the server
			// answers 409 if they are already in, and the page handles that.
			return 'join'
	}
}

/** The visitor's trip matching the invitation's code, if they are already in it. */
export const isMemberOf = <T extends { code: string }>(trips: T[], tripCode: string): T | null => {
	const wanted = tripCode.trim().toUpperCase()
	return trips.find(trip => trip.code.toUpperCase() === wanted) ?? null
}

/** What the page knows about membership, from the state of its trips query. */
export const deriveMembership = <T extends { code: string }>(input: {
	signedIn: boolean
	/** The trips query is in flight with nothing cached yet. */
	isLoading: boolean
	trips: T[] | undefined
	tripCode: string | undefined
}): { status: MembershipStatus; memberTrip: T | null } => {
	if (!input.signedIn) return { status: 'unknown', memberTrip: null }
	if (input.isLoading) return { status: 'loading', memberTrip: null }
	if (!input.trips || !input.tripCode) return { status: 'unknown', memberTrip: null }
	const memberTrip = isMemberOf(input.trips, input.tripCode)
	return { status: memberTrip ? 'member' : 'not-member', memberTrip }
}
